import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import { ChatStorage, ChatMessage, ChatSession, GroupedSessions } from '@app/interfaces';

@Injectable()
export class ConversationHistoryStorageService {
    private readonly sessionsListChanged$: Subject<boolean> = new Subject<boolean>();
    private readonly conversationStorage: ChatStorage = {};
    private sessionId: number = -1;
    private readonly STORAGE_CONVERSATION_HISTORY_SUFFIX = 'streaming_conversation_history';
    private readonly STORAGE_CONVERSATION_MESSAGES_SUFFIX = 'streaming_conversation_messages';

    addNewConversation(sessionId?: number): number {
        const userId: string = this.getCurrentUserId();

        this.sessionId = sessionId || Date.now();
        this.conversationStorage[this.sessionId] = [...this.getStoredConversationMessages(userId, this.sessionId)];

        return this.sessionId;
    }

    addMessageToConversationHistory(message: ChatMessage, sessionId: number = this.sessionId): void {
        if (!this.conversationStorage[sessionId]) {
            this.conversationStorage[sessionId] = [];
        }

        this.conversationStorage[sessionId].push(message);
        this.saveMessagesToLocalStorage(this.conversationStorage[sessionId], sessionId);
    }

    getCurrentConversationId(): number {
        return this.sessionId;
    }

    getCurrentConversationMessages(): ChatMessage[] {
        return this.conversationStorage[this.sessionId] || [];
    }

    clearCurrentConversation(): void {
        // console.log('🧹 [ConversationHistoryStorage] Clearing current conversation');
        if (this.sessionId !== -1) {
            this.conversationStorage[this.sessionId] = [];
            const userId = this.getCurrentUserId();
            this.setStoredConversationMessages(userId, this.sessionId, []);
        }
    }

    startNewSession(): number {
        // console.log('🚀 [ConversationHistoryStorage] Starting new session');
        this.sessionId = Date.now();
        this.conversationStorage[this.sessionId] = [];
        return this.sessionId;
    }

    getConversationMessagesById(sessionId: number): ChatMessage[] {
        if (!this.conversationStorage[sessionId]) {
            // Try to load from localStorage if not in memory
            const userId = this.getCurrentUserId();
            this.conversationStorage[sessionId] = this.getStoredConversationMessages(userId, sessionId);
        }
        return this.conversationStorage[sessionId] || [];
    }

    getGroupedConversationSessions(): GroupedSessions[] {
        const userId: string = this.getCurrentUserId();
        return this.groupSessionsByMonth(this.getStoredSessions(userId));
    }

    changeSessionsList(): void {
        this.sessionsListChanged$.next(true);
    }

    conversationSessionsListChange(): Observable<boolean> {
        return this.sessionsListChanged$.asObservable();
    }

    private getCurrentUserId(): string {
        // For streaming view, we'll use a simple identifier
        // This isolates streaming conversation history from main app
        return 'streaming_user';
    }

    private getStoredSessions(userId: string): ChatSession[] {
        try {
            const stored = localStorage.getItem(`${userId}_${this.STORAGE_CONVERSATION_HISTORY_SUFFIX}`);
            return stored ? JSON.parse(stored) : [];
        } catch (error) {
            console.error('🔴 Failed to load sessions from localStorage:', error);
            return [];
        }
    }

    private setStoredSessions(userId: string, sessions: ChatSession[]): void {
        try {
            localStorage.setItem(`${userId}_${this.STORAGE_CONVERSATION_HISTORY_SUFFIX}`, JSON.stringify(sessions));
        } catch (error) {
            console.error('🔴 Failed to save sessions to localStorage:', error);
        }
    }

    private getStoredConversationMessages(userId: string, sessionId: number): ChatMessage[] {
        try {
            const stored = localStorage.getItem(`${userId}_${sessionId}_${this.STORAGE_CONVERSATION_MESSAGES_SUFFIX}`);
            return stored ? JSON.parse(stored) : [];
        } catch (error) {
            console.error('🔴 Failed to load messages from localStorage:', error);
            return [];
        }
    }

    private setStoredConversationMessages(userId: string, sessionId: number, messages: ChatMessage[]): void {
        try {
            localStorage.setItem(
                `${userId}_${sessionId}_${this.STORAGE_CONVERSATION_MESSAGES_SUFFIX}`,
                JSON.stringify(messages)
            );
        } catch (error) {
            console.error('🔴 Failed to save messages to localStorage:', error);
        }
    }

    private saveMessagesToLocalStorage(messages: ChatMessage[], sessionId: number, tryCount: number = 0): void {
        const userId: string = this.getCurrentUserId();

        try {
            this.saveSessionToLocalStorage(messages.length ? messages[0].content : '', userId, sessionId);
            this.setStoredConversationMessages(userId, sessionId, messages);
        } catch (error: any) {
            console.error('🔴 Failed to save to localStorage:', error);

            if (this.isStorageExceededError(error)) {
                console.log('🔴 Storage exceeded, cleaning up old sessions');

                const newrelic = (window as any).newrelic;
                // Add New Relic page event for local storage quota exceeded
                if (typeof newrelic !== 'undefined') {
                    newrelic.addPageAction('local_storage_quota_exceeded', {
                        session_id: sessionId,
                        user_id: userId,
                        messages_count: messages.length,
                        storage_items: localStorage.length
                    });
                }

                this.cleanupOldSessions();

                if (tryCount === 0) {
                    this.saveMessagesToLocalStorage(messages, sessionId, tryCount + 1);
                }
            }
        }
    }

    private saveSessionToLocalStorage(title: string, userId: string, sessionId: number): void {
        const sessions: ChatSession[] = this.getStoredSessions(userId);

        if (!sessions.length || sessions.findIndex(({ id }: ChatSession) => id === sessionId) === -1) {
            const createDate: string = new Date(sessionId).toISOString();

            sessions.push({ id: sessionId, title, createDate });

            this.setStoredSessions(userId, sessions);
            this.changeSessionsList();
        }
    }

    private groupSessionsByMonth(sessions: ChatSession[]): GroupedSessions[] {
        const reversedSessions: ChatSession[] = [...sessions].reverse();
        const map: Map<string, ChatSession[]> = new Map();

        for (const session of reversedSessions) {
            const key: string = new Date(session.createDate).toLocaleString('en-US', {
                month: 'long',
                year: 'numeric'
            });

            if (!map.has(key)) {
                map.set(key, []);
            }

            map.get(key)!.push(session);
        }

        return Array.from(map.entries()).map(([monthYear, sessions]: [string, ChatSession[]]) => ({
            monthYear,
            sessions
        }));
    }

    private isStorageExceededError(error: any): boolean {
        return error?.name === 'QuotaExceededError' || error?.code === 22;
    }

    private cleanupOldSessions(): void {
        const userId: string = this.getCurrentUserId();
        const sessions: ChatSession[] = this.getStoredSessions(userId);

        if (sessions.length > 50) {
            // Keep only the 30 most recent sessions
            const sortedSessions = sessions.sort(
                (a, b) => new Date(b.createDate).getTime() - new Date(a.createDate).getTime()
            );
            const sessionsToKeep = sortedSessions.slice(0, 30);
            const sessionsToDelete = sortedSessions.slice(30);

            // Delete old session messages
            sessionsToDelete.forEach((session) => {
                localStorage.removeItem(`${userId}_${session.id}_${this.STORAGE_CONVERSATION_MESSAGES_SUFFIX}`);
            });

            // Update sessions list
            this.setStoredSessions(userId, sessionsToKeep);
            this.changeSessionsList();

            // console.log(`🧹 Cleaned up ${sessionsToDelete.length} old conversation sessions`);
        }
    }

    // Backward compatibility methods - delegate to conversation methods
    addNewChat(sessionId?: number): number {
        return this.addNewConversation(sessionId);
    }

    addMessageToChatHistory(message: ChatMessage, sessionId: number = this.sessionId): void {
        this.addMessageToConversationHistory(message, sessionId);
    }

    getCurrentChatId(): number {
        return this.getCurrentConversationId();
    }

    getCurrentChatMessages(): ChatMessage[] {
        return this.getCurrentConversationMessages();
    }

    getChatMessagesById(sessionId: number): ChatMessage[] {
        return this.getConversationMessagesById(sessionId);
    }

    getGroupedChatSessions(): GroupedSessions[] {
        return this.getGroupedConversationSessions();
    }

    chatSessionsListChange(): Observable<boolean> {
        return this.conversationSessionsListChange();
    }
}
